/* eslint-disable no-underscore-dangle */
import { AlgoliaTemplate, AlgoliaDataUtils } from '@algolia/templates';
import { AlgoliaUtils } from '@algolia/utils';
import { AlgoliaUiComponents } from '@algolia/widgets';
import { FacetMetadata } from '@algolia/facets-metadata';

function resolveCardsView(config = {}) {
  const sectionDefault = config.cardsView === 'list' ? 'list' : 'grid';

  if (config.showViewAs === false) {
    return sectionDefault;
  }

  const storedView = localStorage.getItem('searchViewMode');
  if (storedView === 'list' || storedView === 'grid') {
    return storedView;
  }

  return sectionDefault;
}

function syncHitsViewClass(config = {}) {
  const hits = document.querySelector('#hits');
  if (!hits) return;

  const view = resolveCardsView(config);
  hits.classList.remove('search-products--grid', 'search-products--list');
  hits.classList.add(`search-products--${view}`);
}

// Panels the shopper closed by hand stay closed, the automatic opening below
// must not override that. Reopening by hand clears the flag again. The listener
// uses the capture phase because some panel headers stop propagation, so it sees
// the state BEFORE the toggle handler runs: an open panel is about to be closed.
document.addEventListener(
  'click',
  (event) => {
    const header = event.target.closest('.filter-dropdown-item__header');
    if (!header) return;
    const panel = header.closest('.filter-dropdown-item');
    if (!panel) return;
    const body = panel.querySelector('.filter-dropdown-item__body');
    if (body && body.style.display !== 'none') {
      panel.dataset.userToggled = 'true';
    } else {
      delete panel.dataset.userToggled;
    }
  },
  true
);

function openRefinedFacetPanels() {
  const panels = document.querySelectorAll(
    '#facets .filter-dropdown-item, #facets-mobile .filter-dropdown-item'
  );
  panels.forEach((panel) => {
    if (panel.dataset.userToggled === 'true') return;
    const body = panel.querySelector('.filter-dropdown-item__body');
    if (!body || body.style.display !== 'none') return;
    // Checkbox facets mark selections via checked inputs, the category tree via data-refined
    if (!panel.querySelector('input:checked, [data-refined="true"]')) return;
    body.style.display = '';
    panel.classList.remove('filter-dropdown-item--closed');
    panel.classList.add('filter-dropdown-item--open');
    const icon = panel.querySelector('.filter-dropdown-item__icon');
    if (icon) {
      icon.style.transform = 'rotate(0deg)';
      icon.style.transition = 'transform 0.3s ease';
    }
  });
}

// Every drawer opening starts from the canonical state: panels holding an active
// refinement are open, all other panels are closed, and manual toggles from the
// previous drawer session are forgotten.
document.addEventListener('click', (event) => {
  if (!event.target.closest('.filter-sorting-btn')) return;
  document.querySelectorAll('#facets-mobile .filter-dropdown-item').forEach((panel) => {
    delete panel.dataset.userToggled;
    const body = panel.querySelector('.filter-dropdown-item__body');
    if (!body || body.style.display === 'none') return;
    body.style.display = 'none';
    panel.classList.add('filter-dropdown-item--closed');
    panel.classList.remove('filter-dropdown-item--open');
    const icon = panel.querySelector('.filter-dropdown-item__icon');
    if (icon) {
      icon.style.transform = 'rotate(-180deg)';
      icon.style.transition = 'transform 0.3s ease';
    }
  });
  openRefinedFacetPanels();
});

const RESULTS_SCROLL_CLEARANCE = 16;

function getResultsScrollTop() {
  const target = document.querySelector('.collection-products__wrapper');
  if (!target) return null;

  // offsetTop is relative to the nearest positioned ancestor, not the document,
  // so the position has to be derived from the viewport rect plus the scroll offset.
  const documentTop = target.getBoundingClientRect().top + window.scrollY;

  // The sticky header slides back in while scrolling up, which is exactly what this
  // scroll does, so its height has to stay clear above the target.
  let headerOffset = 0;
  const header = document.querySelector('header-component');
  if (header && header.hasAttribute('sticky')) {
    const headerHeight = parseFloat(
      getComputedStyle(document.body).getPropertyValue('--header-height')
    );
    headerOffset = Number.isFinite(headerHeight) ? headerHeight : header.offsetHeight;
  }

  return Math.max(0, documentTop - headerOffset - RESULTS_SCROLL_CLEARANCE);
}

// URL parameter name for a facet attribute, e.g. "meta.custom.anzahl_tueren" -> "anzahl-tueren".
// Returns '' for attributes that have their own dedicated parameter.
function attributeToRouteKey(attribute) {
  if (attribute.startsWith('meta.custom.weitere_informationen.')) {
    return attribute
      .replace('meta.custom.weitere_informationen.', '')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]/g, '');
  }
  if (attribute.startsWith('meta.custom.')) {
    return attribute.replace('meta.custom.', '').toLowerCase().replace(/_/g, '-');
  }
  return '';
}

// The history router parses repeated or bracketed keys (?vendor=a&vendor=b, ?vendor[x]=1) into
// arrays and objects. Anything that is not a plain string would make .split() throw inside
// routeToState and leave the collection empty, so repeated keys are merged into a
// comma-separated list and everything else is ignored.
/** @param {unknown} value */
function routeParamToString(value) {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.filter((entry) => typeof entry === 'string').join(',');
  return '';
}

// Inverse of attributeToRouteKey for every facet the theme knows about
function buildRouteKeyToAttributeMap() {
  const map = {};
  Object.keys(FacetMetadata?.FACET_METADATA || {}).forEach((attribute) => {
    const key = attributeToRouteKey(attribute);
    if (key) map[key] = attribute;
  });
  return map;
}

// Filters of the collection the shopper left last, read by the breadcrumbs on the PDP
// so that "back to collection" restores the active filters. They live only as long as the
// shopper stays on filtered collection pages: any collection page without filters clears
// them. Keep the key in sync with breadcrumbs-element.js.
const PLP_FILTERS_STORAGE_KEY = 'gh:last-plp-filters';

// Takes the route object produced by stateToRoute instead of reading location.search: the
// router writes the URL with a delay, and the route only contains keys the router knows,
// so tracking parameters (gclid, utm_*) never end up in the breadcrumb links.
/** @param {object} route */
function persistCollectionFilters(route) {
  try {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    if (!match) return;
    const search = Object.entries(route || {})
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    if (search) {
      sessionStorage.setItem(
        PLP_FILTERS_STORAGE_KEY,
        JSON.stringify({ handle: match[1], search: `?${search}` })
      );
    } else {
      // A collection page without filters drops whatever is stored, including the filters
      // of another collection. Otherwise they would reappear later on a collection they
      // were never applied to in between (Collection2 -> Collection3 -> Collection2).
      sessionStorage.removeItem(PLP_FILTERS_STORAGE_KEY);
    }
  } catch {
    // storage unavailable (private mode, blocked site data), filters just won't be restored
  }
}

class AlgoliaSearchComponent {
  constructor(ns) {
    this.ns = ns;
    this.search = null;
    this.widgets = [];
    this.initExports();
  }

  async createSearchInstance(config = {}) {
    const {
      renderRangeSlider,
      renderSortBy,
      createRefinementListWithHeader,
      createGroupedNumericRefinementList,
      createExpressTagRefinement,
      renderCurrentRefinements,
      openFirstFiveFacets,
      renderPagination,
      renderStats,
      createHierarchicalMenuWithHeader,
    } = AlgoliaUiComponents;
    try {
      // UZ-8543: resolves right away on collection/search pages, loads the libraries elsewhere
      await window.loadAlgoliaLibs?.();
      if (!window.algoliasearch || !window.instantsearch) {
        throw new Error('Required search dependencies not loaded');
      }

      // Determine page type
      let pageType = 'default';
      if (window.location.pathname.includes('/search') || config.useVirtualSearchBox) {
        pageType = 'search';
      } else if (window.location.pathname.includes('/collections') || !!config.filters) {
        pageType = 'collection';
      }

      // Custom search client with analytics tagging
      const algoliaClient = window.algoliasearch.algoliasearch(
        window.algoliaConfig.appId || '{{ settings.algolia_app_id }}',
        window.algoliaConfig.apiKey || '{{ settings.algolia_api_key }}'
      );
      const searchClient = {
        ...algoliaClient,
        search(requests) {
          if (!requests || requests.length === 0) {
            return algoliaClient.search(requests);
          }
          const modifiedRequests = requests.map((request) => {
            const query = request.params?.query || '';
            const isEmptyQuery = query.trim().length === 0;
            if (pageType === 'collection') {
              return {
                ...request,
                params: {
                  ...request.params,
                  analytics: true,
                  clickAnalytics: true,
                  analyticsTags: isEmptyQuery ? ['category-browsing'] : ['category-search'],
                },
              };
            }
            if (isEmptyQuery) {
              return {
                ...request,
                params: {
                  ...request.params,
                  analytics: false,
                },
              };
            }
            return {
              ...request,
              params: {
                ...request.params,
                analytics: true,
                clickAnalytics: true,
                analyticsTags: ['typed-search'],
              },
            };
          });
          return algoliaClient.search(modifiedRequests);
        },
      };

      // Get search query from URL parameters if useUrlQuery is enabled
      const urlParams = new URLSearchParams(window.location.search);
      const initialQuery = config.useUrlQuery ? urlParams.get('q') || '' : '';

      // Build initial UI state
      const initialUiState = config.useUrlQuery
        ? {
            [window.algoliaConfig.indexName]: {
              query: initialQuery,
            },
          }
        : undefined;

      // Routing configuration
      const routing = {
        stateMapping: {
          stateToRoute(uiState) {
            const indexUiState = uiState[window.algoliaConfig.indexName] || {};
            const route = {};
            if (indexUiState.query) {
              route.q = indexUiState.query;
            }
            if (indexUiState.sortBy && indexUiState.sortBy !== window.algoliaConfig.indexName) {
              const sortMap = {
                [`${window.algoliaConfig.indexName}_bestsellers`]: 'bestsellers',
                [`${window.algoliaConfig.indexName}_title_asc`]: 'title-asc',
                [`${window.algoliaConfig.indexName}_title_desc`]: 'title-desc',
                [`${window.algoliaConfig.indexName}_price_asc`]: 'price-asc',
                [`${window.algoliaConfig.indexName}_price_desc`]: 'price-desc',
              };
              route.sort = sortMap[indexUiState.sortBy] || indexUiState.sortBy;
            }
            if (indexUiState.range && indexUiState.range.price) {
              const priceRange = indexUiState.range.price;
              if (typeof priceRange === 'string' && priceRange.includes(':')) {
                const [min, max] = priceRange.split(':');
                route.price = `${min || ''}-${max || ''}`;
              } else if (typeof priceRange === 'object') {
                if (
                  (priceRange.min !== undefined && priceRange.min !== null) ||
                  (priceRange.max !== undefined && priceRange.max !== null)
                ) {
                  route.price = `${priceRange.min !== undefined ? priceRange.min : ''}-${priceRange.max !== undefined ? priceRange.max : ''}`;
                }
              }
            } else if (indexUiState.numericMenu && indexUiState.numericMenu.price) {
              const priceRange = indexUiState.numericMenu.price;
              if (
                (priceRange.min !== undefined && priceRange.min !== null) ||
                (priceRange.max !== undefined && priceRange.max !== null)
              ) {
                route.price = `${priceRange.min !== undefined ? priceRange.min : ''}-${priceRange.max !== undefined ? priceRange.max : ''}`;
              }
            }
            if (indexUiState.refinementList) {
              if (indexUiState.refinementList.collections) {
                route.collections = indexUiState.refinementList.collections.join(',');
              }
              if (indexUiState.refinementList.vendor) {
                route.vendor = indexUiState.refinementList.vendor.join(',');
              }
              if (indexUiState.refinementList.product_type) {
                route['product-type'] = indexUiState.refinementList.product_type.join(',');
              }
              Object.keys(indexUiState.refinementList).forEach((attribute) => {
                if (
                  attribute === 'collections' ||
                  attribute === 'vendor' ||
                  attribute === 'product_type'
                ) {
                  return;
                }
                const simplifiedName = attributeToRouteKey(attribute);
                if (simplifiedName && indexUiState.refinementList[attribute].length > 0) {
                  route[simplifiedName] = indexUiState.refinementList[attribute].join(',');
                }
              });
            }
            if (indexUiState.page && indexUiState.page > 1) {
              route.page = indexUiState.page;
            }
            return route;
          },
          routeToState(routeState) {
            const state = {
              [window.algoliaConfig.indexName]: {},
            };
            /** @param {string} key */
            const param = (key) => routeParamToString(routeState[key]);
            if (param('q')) {
              state[window.algoliaConfig.indexName].query = param('q');
            }
            if (param('sort')) {
              /** @type {Record<string, string>} */
              const sortMap = {
                bestsellers: `${window.algoliaConfig.indexName}_bestsellers`,
                'title-asc': `${window.algoliaConfig.indexName}_title_asc`,
                'title-desc': `${window.algoliaConfig.indexName}_title_desc`,
                'price-asc': `${window.algoliaConfig.indexName}_price_asc`,
                'price-desc': `${window.algoliaConfig.indexName}_price_desc`,
              };
              state[window.algoliaConfig.indexName].sortBy =
                sortMap[param('sort')] || window.algoliaConfig.indexName;
            }
            if (param('price')) {
              const [minStr, maxStr] = param('price').split('-');
              let priceRangeStr = '';
              if (minStr || maxStr) {
                priceRangeStr = `${minStr || ''}:${maxStr || ''}`;
              }
              if (priceRangeStr) {
                state[window.algoliaConfig.indexName].range = {
                  price: priceRangeStr,
                };
              }
            }
            state[window.algoliaConfig.indexName].refinementList = {};
            if (param('collections')) {
              state[window.algoliaConfig.indexName].refinementList.collections =
                param('collections').split(',');
            }
            if (param('vendor')) {
              state[window.algoliaConfig.indexName].refinementList.vendor =
                param('vendor').split(',');
            }
            if (param('product-type')) {
              state[window.algoliaConfig.indexName].refinementList.product_type =
                param('product-type').split(',');
            }
            // Legacy keys keep old bookmarks working; the generated map is built with the
            // same slug function as stateToRoute, so every facet of the active layout
            // (including the category tree) round-trips through the URL.
            const legacyAttributeMap = {
              'express-tag': 'meta.custom.express_tag',
              'anschluss-in-volt': 'meta.custom.weitere_informationen.Anschluss in Volt',
              'anzahl-kochstellen': 'meta.custom.weitere_informationen.Anzahl Kochstellen',
              'anzahl-tueren': 'meta.custom.weitere_informationen.Anzahl Türen',
              aufkantung: 'meta.custom.weitere_informationen.Aufkantung',
              'breite-in-mm': 'meta.custom.weitere_informationen.Breite in mm',
              'durchmesser-des-schneidmessers':
                'meta.custom.weitere_informationen.Durchmesser des Schneidmessers',
              enthaerter: 'meta.custom.weitere_informationen.Enthärter',
              'gn-mass': 'meta.custom.weitere_informationen.GN Maß',
              geraetetyp: 'meta.custom.weitere_informationen.Gerätetyp',
              grundboden: 'meta.custom.weitere_informationen.Grundboden',
              'hoehe-in-mm': 'meta.custom.weitere_informationen.Höhe in mm',
              kochart: 'meta.custom.weitere_informationen.Kochart',
              'korbmasse-in-mm': 'meta.custom.weitere_informationen.Korbmaße in mm',
              kuehlbereich: 'meta.custom.weitere_informationen.Kühlbereich',
              kuehlung: 'meta.custom.weitere_informationen.Kühlung',
              'kuehlvolumen-liter': 'meta.custom.weitere_informationen.Kühlvolumen [Liter]',
              laugenpumpe: 'meta.custom.weitere_informationen.Laugenpumpe',
              material: 'meta.custom.weitere_informationen.Material',
              montageform: 'meta.custom.weitere_informationen.Montageform',
              spuelbeckenanzahl: 'meta.custom.weitere_informationen.Spülbeckenanzahl',
              'tiefe-in-mm': 'meta.custom.weitere_informationen.Tiefe in mm',
              tuerart: 'meta.custom.weitere_informationen.Türart',
              'chair-type': 'meta.custom.weitere_informationen.chair_type',
              klappbar: 'meta.custom.weitere_informationen.klappbar',
              serie: 'meta.custom.weitere_informationen.Serie',
              spuelmaschinenart: 'meta.custom.weitere_informationen.spuelmaschinenart',
              stapelfaehig: 'meta.custom.weitere_informationen.stapelfähig',
              tischform: 'meta.custom.weitere_informationen.tischform',
            };
            const attributeMap = { ...legacyAttributeMap, ...buildRouteKeyToAttributeMap() };
            Object.keys(routeState).forEach((key) => {
              if (attributeMap[key] && param(key)) {
                state[window.algoliaConfig.indexName].refinementList[attributeMap[key]] =
                  param(key).split(',');
              }
            });
            const page = Number(param('page'));
            if (page > 0) {
              state[window.algoliaConfig.indexName].page = page;
            }
            return state;
          },
        },
      };

      const search = window.instantsearch({
        indexName: window.algoliaConfig.indexName,
        searchClient: searchClient,
        routing,
        ...(initialUiState && { initialUiState }),
        onStateChange: ({ uiState, setUiState }) => {
          try {
            setUiState(uiState);
            if (pageType === 'collection') {
              persistCollectionFilters(routing.stateMapping.stateToRoute(uiState));
            }
            const targetTop = getResultsScrollTop();
            if (targetTop === null) return;
            if (window.scrollY > targetTop) {
              window.scrollTo({
                top: targetTop,
                behavior: 'smooth',
              });
            }
          } catch (error) {
            AlgoliaUtils.logError(error, 'onStateChange');
          }
        },
        clickAnalytics: true,
      });

      // Widget connectors
      const customRangeSlider = window.instantsearch.connectors.connectRange(renderRangeSlider);
      const customSortBy = window.instantsearch.connectors.connectSortBy(renderSortBy);
      const customStats = window.instantsearch.connectors.connectStats(renderStats);

      const widgets = [];

      // Virtual search box
      if (config.useVirtualSearchBox) {
        const virtualSearchBox = window.instantsearch.connectors.connectSearchBox(() => {});
        widgets.push(virtualSearchBox());
      }

      // Configure widget
      if (config.filters) {
        widgets.push(
          window.instantsearch.widgets.configure({
            filters: config.filters,
            hitsPerPage: 20,
          })
        );
      } else {
        widgets.push(
          window.instantsearch.widgets.configure({
            hitsPerPage: 20,
          })
        );
      }

      // Current refinements
      const customCurrentRefinements =
        window.instantsearch.connectors.connectCurrentRefinements(renderCurrentRefinements);
      widgets.push(
        customCurrentRefinements({
          container: '#current-refinements',
        })
      );

      // Same chip list inside the mobile filter drawer; the single reset lives in
      // the drawer footer, so the inline reset button stays desktop-only.
      widgets.push(
        customCurrentRefinements({
          container: '#current-refinements-mobile',
          showResetButton: false,
          footerResetSelector: '.js-drawer-reset-filters',
        })
      );

      // Stats widget
      widgets.push(customStats());

      // Hits and pagination
      widgets.push(
        window.instantsearch.widgets.hits({
          container: '#hits',
          templates: {
            item: (hit) => {
              const currentView = resolveCardsView(config);
              const useAlternativeList =
                currentView === 'list' && AlgoliaTemplate.isAlternativeListViewEnabled();
              const template =
                currentView === 'list'
                  ? useAlternativeList
                    ? AlgoliaTemplate.hitTemplateListAlt(
                        hit,
                        config.productTitleTag ? config.productTitleTag : 'span'
                      )
                    : AlgoliaTemplate.hitTemplateList(
                        hit,
                        config.productTitleTag ? config.productTitleTag : 'span'
                      )
                  : AlgoliaTemplate.hitTemplate(
                      hit,
                      config.productTitleTag ? config.productTitleTag : 'span'
                    );
              setTimeout(() => {
                if (AlgoliaDataUtils.resolveEnergyLabelGids)
                  AlgoliaDataUtils.resolveEnergyLabelGids();
                AlgoliaTemplate.loadVendorLogosMap?.();
              }, 0);
              return template;
            },
            empty: (results) => {
              const hasDisjunctiveFacets =
                results._state.disjunctiveFacetsRefinements &&
                Object.keys(results._state.disjunctiveFacetsRefinements).some(
                  (key) => results._state.disjunctiveFacetsRefinements[key].length > 0
                );
              const hasNumericRefinements =
                results._state.numericRefinements &&
                Object.keys(results._state.numericRefinements).some(
                  (key) =>
                    results._state.numericRefinements[key] &&
                    Object.keys(results._state.numericRefinements[key]).length > 0
                );
              const hasHierarchicalFacets =
                results._state.hierarchicalFacetsRefinements &&
                Object.keys(results._state.hierarchicalFacetsRefinements).some(
                  (key) => results._state.hierarchicalFacetsRefinements[key].length > 0
                );
              const hasActiveFilters =
                hasDisjunctiveFacets || hasNumericRefinements || hasHierarchicalFacets;
              if (hasActiveFilters) {
                const currentRefinements = document.querySelector('#current-refinements');
                if (currentRefinements) {
                  currentRefinements.style.display = 'block';
                }
                const currentRefinementsMobile = document.querySelector(
                  '#current-refinements-mobile'
                );
                if (currentRefinementsMobile) {
                  currentRefinementsMobile.style.display = 'block';
                }
                if (window.innerWidth <= 768) {
                  const filterHeader = document.querySelector('.filter-header');
                  const button = filterHeader?.querySelector('.filter-sorting-btn');
                  if (button) {
                    button.style.display = 'block';
                  }
                }
              }
              return AlgoliaTemplate.errorTemplate(
                undefined,
                undefined,
                pageType,
                hasActiveFilters
              );
            },
          },
          transformItems: (items, { results } = {}) => {
            try {
              // PP-1011: warranty add-ons never get a card
              const validItems = items.filter(
                (item) => item && item.handle && item.template_suffix !== 'warranty'
              );

              if (pageType === 'collection') {
                AlgoliaTemplate.updateItemListSchema(validItems, {
                  page: results?.page,
                  hitsPerPage: results?.hitsPerPage,
                });
              }

              return validItems;
            } catch (error) {
              AlgoliaUtils.logError(error, 'transformItems');
              return [];
            }
          },
        }),
        ...(document.querySelector('#pagination')
          ? [
              window.instantsearch.connectors.connectPagination(renderPagination)({
                container: '#pagination',
                padding: 2,
              }),
            ]
          : []),
        ...(document.querySelector('#pagination-bottom')
          ? [
              window.instantsearch.connectors.connectPagination(renderPagination)({
                container: '#pagination-bottom',
                padding: 2,
              }),
            ]
          : []),
        customSortBy({
          container: '#sorting',
          items: [
            {
              value: window.algoliaConfig.indexName,
              label: window.searchTranslations.sort.recommended,
            },
            {
              value: `${window.algoliaConfig.indexName}_bestsellers`,
              label: window.searchTranslations.sort.bestsellers,
            },
            {
              value: `${window.algoliaConfig.indexName}_title_asc`,
              label: window.searchTranslations.sort.titleAsc,
            },
            {
              value: `${window.algoliaConfig.indexName}_title_desc`,
              label: window.searchTranslations.sort.titleDesc,
            },
            {
              value: `${window.algoliaConfig.indexName}_price_asc`,
              label: window.searchTranslations.sort.priceAsc,
            },
            {
              value: `${window.algoliaConfig.indexName}_price_desc`,
              label: window.searchTranslations.sort.priceDesc,
            },
          ],
        }),
        customSortBy({
          container: '#sorting-mobile',
          items: [
            {
              value: window.algoliaConfig.indexName,
              label: window.searchTranslations.sort.recommended,
            },
            {
              value: `${window.algoliaConfig.indexName}_bestsellers`,
              label: window.searchTranslations.sort.bestsellers,
            },
            {
              value: `${window.algoliaConfig.indexName}_title_asc`,
              label: window.searchTranslations.sort.titleAsc,
            },
            {
              value: `${window.algoliaConfig.indexName}_title_desc`,
              label: window.searchTranslations.sort.titleDesc,
            },
            {
              value: `${window.algoliaConfig.indexName}_price_asc`,
              label: window.searchTranslations.sort.priceAsc,
            },
            {
              value: `${window.algoliaConfig.indexName}_price_desc`,
              label: window.searchTranslations.sort.priceDesc,
            },
          ],
        }),
        window.instantsearch.widgets.dynamicWidgets({
          container: '#facets',
          facets: ['*'],
          maxValuesPerFacet: 2000,
          transformItems: (items) => items,
          widgets: [
            (container) => customRangeSlider({ container, attribute: 'price' }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: 'collections',
                label:
                  FacetMetadata?.getFacetTranslation('collections') ||
                  window.searchTranslations?.filters?.collections ||
                  'Collections',
                searchable: true,
              }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: 'vendor',
                label:
                  FacetMetadata?.getFacetTranslation('manufacturer') ||
                  window.searchTranslations?.filters?.manufacturer ||
                  'Manufacturer',
              }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: 'product_type',
                label:
                  FacetMetadata?.getFacetTranslation('productType') ||
                  window.searchTranslations?.filters?.productType ||
                  'Product Type',
              }),
            (container) =>
              createExpressTagRefinement({
                container,
                attribute: 'meta.custom.express_tag',
                label:
                  FacetMetadata?.getFacetTranslation('expressShipping') ||
                  window.searchTranslations?.filters?.expressShipping ||
                  'Express Shipping',
              }),
            ...(config.showCategoryFilter !== false
              ? [
                  (container) =>
                    createHierarchicalMenuWithHeader({
                      container,
                      attribute: 'meta.custom.display_category',
                      label: FacetMetadata?.getFacetTranslation('kategorien') || 'Kategorien',
                      limit: 1000,
                      showMore: true,
                      showMoreLimit: 2000,
                    }),
                ]
              : []),
            ...(FacetMetadata
              ? (() => {
                  const metadataEntries = Object.entries(FacetMetadata.FACET_METADATA).filter(
                    ([key]) =>
                      key.startsWith('meta.custom.') &&
                      key !== 'meta.custom.express_tag' &&
                      key !== 'meta.custom.display_category'
                  );
                  return metadataEntries.map(([attribute, metadata]) => (container) => {
                    const label = FacetMetadata.getFacetTranslation(metadata.translationKey);
                    if (metadata.type === 'grouped_numeric') {
                      return createGroupedNumericRefinementList({
                        container,
                        attribute,
                        label,
                        breakpoints: metadata.breakpoints,
                      });
                    }
                    return createRefinementListWithHeader({
                      container,
                      attribute,
                      label,
                    });
                  });
                })()
              : []),
          ],
        }),
        window.instantsearch.widgets.dynamicWidgets({
          container: '#facets-mobile',
          facets: ['*'],
          maxValuesPerFacet: 2000,
          transformItems: (items) => items,
          widgets: [
            (container) => customRangeSlider({ container, attribute: 'price' }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: 'collections',
                label:
                  FacetMetadata?.getFacetTranslation('collections') ||
                  window.searchTranslations?.filters?.collections ||
                  'Collections',
                searchable: true,
              }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: 'vendor',
                label:
                  FacetMetadata?.getFacetTranslation('manufacturer') ||
                  window.searchTranslations?.filters?.manufacturer ||
                  'Manufacturer',
              }),
            (container) =>
              createRefinementListWithHeader({
                container,
                attribute: 'product_type',
                label:
                  FacetMetadata?.getFacetTranslation('productType') ||
                  window.searchTranslations?.filters?.productType ||
                  'Product Type',
              }),
            (container) =>
              createExpressTagRefinement({
                container,
                attribute: 'meta.custom.express_tag',
                label:
                  FacetMetadata?.getFacetTranslation('expressShipping') ||
                  window.searchTranslations?.filters?.expressShipping ||
                  'Express Shipping',
              }),
            ...(config.showCategoryFilter !== false
              ? [
                  (container) =>
                    createHierarchicalMenuWithHeader({
                      container,
                      attribute: 'meta.custom.display_category',
                      label: FacetMetadata?.getFacetTranslation('kategorien') || 'Kategorien',
                      limit: 1000,
                      showMore: true,
                      showMoreLimit: 2000,
                    }),
                ]
              : []),
            ...(FacetMetadata
              ? (() => {
                  const metadataEntries = Object.entries(FacetMetadata.FACET_METADATA).filter(
                    ([key]) =>
                      key.startsWith('meta.custom.') &&
                      key !== 'meta.custom.express_tag' &&
                      key !== 'meta.custom.display_category'
                  );
                  return metadataEntries.map(([attribute, metadata]) => (container) => {
                    const label = FacetMetadata.getFacetTranslation(metadata.translationKey);
                    if (metadata.type === 'grouped_numeric') {
                      return createGroupedNumericRefinementList({
                        container,
                        attribute,
                        label,
                        breakpoints: metadata.breakpoints,
                      });
                    }
                    return createRefinementListWithHeader({
                      container,
                      attribute,
                      label,
                    });
                  });
                })()
              : []),
          ],
        })
      );

      widgets.forEach((widget) => search.addWidget(widget));

      if (config.showViewAs === false) {
        syncHitsViewClass(config);
      }

      search.start();
      this.search = search;
      window.__instantSearchInstance = search;

      if (pageType === 'collection') {
        // onStateChange only fires for changes made on this page, so a collection opened
        // with filters in the URL (shared link, breadcrumb) is saved here, reduced to the
        // known keys by the same round trip the router uses.
        const initialRoute = Object.fromEntries(new URLSearchParams(window.location.search));
        persistCollectionFilters(
          routing.stateMapping.stateToRoute(routing.stateMapping.routeToState(initialRoute))
        );
      }

      search.on('render', () => {
        openFirstFiveFacets();
        openRefinedFacetPanels();
        if (config.showViewAs === false) {
          syncHitsViewClass(config);
        }
      });

      return search;
    } catch (error) {
      AlgoliaUtils.logError(error, 'createSearchInstance');
      throw error;
    }
  }

  async initWithRetry(initializeSearch, maxRetries = 3, delay = 1000, attempt = 1) {
    try {
      return await initializeSearch();
    } catch (error) {
      if (attempt >= maxRetries) {
        AlgoliaUtils.logError(error, `initWithRetry (attempt ${attempt})`);
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      return this.initWithRetry(initializeSearch, maxRetries, delay * 1.5, attempt + 1);
    }
  }

  setInstantSearchUiState(indexUiState) {
    if (!this.search) return;
    this.search.setUiState((prevState) => ({
      ...prevState,
      [window.algoliaConfig.indexName]: {
        ...prevState[window.algoliaConfig.indexName],
        ...indexUiState,
      },
    }));
  }

  // Export methods to the namespace for compatibility
  initExports() {
    const ns = this.ns;
    ns.createSearchInstance = this.createSearchInstance.bind(this);
    ns.initWithRetry = this.initWithRetry.bind(this);
    ns.setInstantSearchUiState = this.setInstantSearchUiState.bind(this);
  }
}
new AlgoliaSearchComponent(window.ProductSearch || (window.ProductSearch = {}));
